import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import api from '../../api/axios';
import CardRenderer, { MM_TO_PX } from './CardRenderer';
import { downloadCardFront, downloadCardBothSides } from '../../utils/cardExport';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Modal "Carte de membre" — seule implémentation dans toute l'application.
 * Résout automatiquement le template assigné au fidèle (ou le défaut publié
 * de son église) et rend recto/verso avec CardRenderer — la même logique
 * que l'éditeur et la prévisualisation.
 * Deux modes : `participantId` (session admin JWT) ou `qrCode` (portail
 * fidèle sans session admin, endpoint public non-authentifié).
 */
export default function MemberCardModal({ participantId, qrCode, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSide, setActiveSide] = useState('front');
  const [downloading, setDownloading] = useState(false);
  const [fieldChoices, setFieldChoices] = useState([]);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const request = qrCode
      ? axios.get(`${API}/cards/public/participant/${qrCode}/`)
      : api.get(`/cards/participant/${participantId}/`);
    request.then(res => {
      if (!cancelled) setData(res.data);
    }).catch(() => {
      if (!cancelled) toast.error('Erreur lors du chargement de la carte.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [participantId, qrCode]);

  useEffect(() => {
    axios.get(`${API}/cards/field-choices/`).then(r => {
      if (r.data?.fields?.length) setFieldChoices(r.data.fields);
    }).catch(() => {});
  }, []);

  const template = data?.template;
  const frontSide = data?.sides?.find(s => s.side === 'front');
  const backSide = data?.sides?.find(s => s.side === 'back');
  const hasBack = !!backSide;
  const widthPx = template ? template.width_mm * MM_TO_PX : 324;
  const heightPx = template ? template.height_mm * MM_TO_PX : 204;

  const memberCode = frontSide?.elements?.find(e => e.field_key === 'member_code')?.resolved_value;

  const handleDownloadFront = async () => {
    if (!frontRef.current) return;
    setDownloading(true);
    try {
      await downloadCardFront(frontRef.current, memberCode);
      toast.success('Carte téléchargée !');
    } catch {
      toast.error('Erreur lors du téléchargement.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadBoth = async () => {
    if (!frontRef.current || !backRef.current) return;
    setDownloading(true);
    try {
      await downloadCardBothSides(frontRef.current, backRef.current, memberCode);
      toast.success('Carte recto-verso téléchargée !');
    } catch {
      toast.error('Erreur lors du téléchargement.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Carte de membre</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1.2rem', lineHeight: 1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Chargement...</div>
        ) : !template ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Aucun template de carte n'est publié pour cette église.
            <div style={{ marginTop: 6, fontSize: '0.78rem' }}>Configurez un template dans Administration → Cartes de membre.</div>
          </div>
        ) : (
          <>
            {hasBack && (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
                <button onClick={() => setActiveSide('front')} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border)', background: activeSide === 'front' ? '#1e293b' : '#fff', color: activeSide === 'front' ? '#fff' : 'var(--text-secondary)', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer' }}>Recto</button>
                <button onClick={() => setActiveSide('back')} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border)', background: activeSide === 'back' ? '#1e293b' : '#fff', color: activeSide === 'back' ? '#fff' : 'var(--text-secondary)', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer' }}>Verso</button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: activeSide === 'front' ? 'block' : 'none' }}>
                <div ref={frontRef} style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                  <CardRenderer side={frontSide} widthPx={widthPx} heightPx={heightPx} fieldChoices={fieldChoices} />
                </div>
              </div>
              {hasBack && (
                <div style={{ display: activeSide === 'back' ? 'block' : 'none' }}>
                  <div ref={backRef} style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                    <CardRenderer side={backSide} widthPx={widthPx} heightPx={heightPx} fieldChoices={fieldChoices} />
                  </div>
                </div>
              )}
              {/* Rendu hors-écran du côté inactif, nécessaire pour l'export recto-verso même si masqué à l'affichage */}
              {hasBack && activeSide === 'front' && (
                <div style={{ position: 'fixed', left: -9999, top: -9999 }}>
                  <div ref={backRef}><CardRenderer side={backSide} widthPx={widthPx} heightPx={heightPx} fieldChoices={fieldChoices} /></div>
                </div>
              )}
              {hasBack && activeSide === 'back' && (
                <div style={{ position: 'fixed', left: -9999, top: -9999 }}>
                  <div ref={frontRef}><CardRenderer side={frontSide} widthPx={widthPx} heightPx={heightPx} fieldChoices={fieldChoices} /></div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <button onClick={handleDownloadFront} disabled={downloading}
                style={{ background: '#1e293b', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: downloading ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                Télécharger le recto
              </button>
              {hasBack && (
                <button onClick={handleDownloadBoth} disabled={downloading}
                  style={{ background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: downloading ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                  Télécharger recto-verso
                </button>
              )}
              {memberCode && (
                <button onClick={() => { navigator.clipboard.writeText(memberCode).then(() => toast.success('Code copié !')).catch(() => {}); }}
                  style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                  Copier le code
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
